import React, {useEffect, useState} from 'react';
import {AutoCompleteMultiInput, DropdownMultiSelect} from "../../common";
import DatePicker from "react-datepicker";
import {DatePickerInput} from "../../common/component/DatePickerInput";
import {SPLIT_DELIMITER} from "../../placement/const";
import {ParentCaseListAction} from "./parentCaseListReducer";
import {tryFormatLocalDate} from "../../common/util/date";
import {CurrentAcitveAuthorizationServiceTypeFilterOption, ParentCaseStatusMapWithDefault} from "./domain";
import {MultiFilterButton, SimpleDropdown} from "common";

export type  ParentCaseListFilterValue = {
    serviceType: string;
    cities: string;
    cpaStatus: string;
    fromDate: Date | null;
    toDate: Date | null;
}

export type ParentCaseListFilterParams = {
    cpaStatus: string | null;
    serviceTypes: Array<string>;
    cities: Array<string>;
    referralAcceptedFrom: String | null;
    referralAcceptedTo: String | null;
}

export function asQueryParam(filter: ParentCaseListFilterValue | undefined): ParentCaseListFilterParams {
    return {
        cpaStatus: filter?.cpaStatus ? filter?.cpaStatus : null,
        serviceTypes: filter?.serviceType ? filter?.serviceType
            .split(SPLIT_DELIMITER)
            .filter(
                (serviceType) => serviceType !== "") : [],
        cities: filter?.cities ? filter?.cities
            .split(SPLIT_DELIMITER)
            .filter(
                (city) => city !== "") : [],
        referralAcceptedFrom: tryFormatLocalDate(filter?.fromDate ?? null),
        referralAcceptedTo: tryFormatLocalDate(filter?.toDate ?? null),
    };
}

type Props = {
    initFilterValue: ParentCaseListFilterValue,
    dispatch: React.Dispatch<ParentCaseListAction>
}

export function ParentCaseListFilter({initFilterValue, dispatch}: Props) {
    const [filterValue, setFilterValue] = useState({...initFilterValue});
    // update internal value when init filter value updated
    useEffect(() => {
        setFilterValue(initFilterValue);
    }, [initFilterValue])

    const onFilterUpdate = (fieldName: string, fieldValue: any) => {
        setFilterValue({...filterValue, [fieldName]: fieldValue})
    }

    return <MultiFilterButton<ParentCaseListFilterValue>
        filterValue={filterValue}
        setFilterValue={setFilterValue}
        dispatchSetFilterValue={(val) => dispatch({type: "SET_FILTER", filter: val})}
        filterOptions={[
            {
                label: "Status",
                component: <SimpleDropdown
                    options={ParentCaseStatusMapWithDefault}
                    selected={filterValue.cpaStatus}
                    minWidth={"7rem"}
                    textColor={"gray-600"}
                    setSelected={(val) => onFilterUpdate("cpaStatus", val)}/>
            },
            {
                label: "Service Type",
                component: <DropdownMultiSelect field='serviceType'
                                               options={Array.from(CurrentAcitveAuthorizationServiceTypeFilterOption.keys())}
                                               labelMapperFunc={(val) => CurrentAcitveAuthorizationServiceTypeFilterOption.get(val)!}
                                               full
                                               value={filterValue.serviceType}
                                               setValue={(values) => onFilterUpdate("serviceType", values)}/>
            },
            {
                label: "City",
                component: <AutoCompleteMultiInput field={'cities'}
                                                   fieldValue={filterValue.cities}
                                                   queryType='city'
                                                   inputType='char-only'
                                                   setFieldValue={(val) => onFilterUpdate("cities", val)}/>
            },
            {
                label: "Placement Date From",
                component: <DatePicker
                    wrapperClassName="w-full"
                    selected={filterValue.fromDate || null}
                    onSelect={(date: Date) => onFilterUpdate("fromDate", date)}
                    onChange={() => {}}
                    customInput={<DatePickerInput
                        fieldName="fromDate"
                        value={undefined}
                        onFinish={(date) => onFilterUpdate("fromDate", date)}
                    />}
                />
            },
            {
                label: "Placement Date To",
                component: <DatePicker
                    wrapperClassName="w-full"
                    selected={filterValue.toDate || null}
                    onSelect={(newValue: Date | null) => onFilterUpdate("toDate", newValue)}
                    onChange={() => {}}
                    customInput={<DatePickerInput
                        fieldName="toDate"
                        value={undefined}
                        onFinish={(date) => onFilterUpdate("toDate", date)}
                    />}
                />
            },
        ]}
        emptyFilterValue={{
            cpaStatus: "",
            serviceType: "",
            cities: '',
            fromDate: null,
            toDate: null,
        }}
    />;
}
